import type { TransformedReport, Segment, AppealStrategy, Banner, CompetitorMap, AppealMap, BannerEvaluation } from './types';

/**
 * Clean text by removing Unicode replacement characters (U+FFFD)
 * that may appear from UTF-8 sanitization side effects.
 */
function cleanText(text: string): string {
  return text.replace(/\uFFFD/g, '').trim();
}

/**
 * Filter out garbled text items (containing U+FFFD or too short after cleaning).
 */
function filterGarbled(items: string[]): string[] {
  return items
    .map(cleanText)
    .filter((s) => s.length > 1);
}

/**
 * Extract the first sentence from a text string.
 */
function extractFirstSentence(text?: string): string {
  if (!text) return '';
  // Match first sentence ending with period (Japanese or English)
  const match = text.match(/^[^.。!！?？]+[.。!！?？]?/);
  return match ? match[0].trim() : text.slice(0, 100);
}

/**
 * Build segments from planning data.
 * Creates one segment from target_audience info.
 */
function buildSegments(planning: any): Segment[] {
  const targetAudience = planning?.ad_strategy?.target_audience ?? planning?.target_audience;
  if (!targetAudience) return [];

  return [
    {
      segment_id: 1,
      name: targetAudience,
      funnel_stage: '',
      visibility: 'public',
      demographics: {
        gender: '',
        age_range: targetAudience,
        occupation: '',
        purchasing_power: '',
      },
      emotion_map: {
        pain: '',
        ideal_state: '',
        barriers: '',
        purchase_drivers: '',
      },
      benefits: {
        functional: planning?.product_features?.features?.[0] ?? planning?.features?.[0] ?? '',
        emotional: planning?.product_features?.benefits?.[0] ?? planning?.benefits?.[0] ?? '',
        self_actualization: '',
      },
    },
  ];
}

/**
 * Build appeal strategies from planning data.
 * Generates strategies from key_copies/headlines.
 */
function buildAppealStrategies(planning: any): AppealStrategy[] {
  const headlines =
    planning?.key_copies?.headlines ?? planning?.headlines ?? [];
  const ctaTexts = planning?.key_copies?.cta_texts ?? planning?.cta_texts ?? [];
  const recommendedMessages =
    planning?.key_copies?.recommended_messages ?? planning?.recommended_messages ?? [];

  if (headlines.length === 0 && recommendedMessages.length === 0) return [];

  const strategies: AppealStrategy[] = [];

  // Create a strategy from headlines
  if (headlines.length > 0) {
    strategies.push({
      segment_id: 0,
      appeal_id: 0,
      name: headlines[0],
      type: '',
      direction: recommendedMessages[0] ?? '',
      strategy_detail: recommendedMessages[0] ?? '',
      copy_examples: headlines.slice(0, 3),
      visibility: 'public',
    });
  }

  // Create additional strategies from CTA texts if available
  if (ctaTexts.length > 0 && headlines.length > 1) {
    strategies.push({
      segment_id: 0,
      appeal_id: 1,
      name: 'CTA',
      type: 'CTA',
      direction: '',
      copy_examples: ctaTexts.slice(0, 3),
      visibility: 'locked',
    });
  }

  return strategies;
}

/**
 * Build banners from banner_session and banner_urls.
 * Maps backend banner data to v0 Banner format.
 */
function buildBanners(
  bannerSession: any,
  bannerUrls?: string[],
  legacyBanners?: any[]
): Banner[] {
  const banners: Banner[] = [];

  // Priority 1: banner_session.banners (has presigned URLs from controller)
  //  (banner_urls contains raw S3 URLs that may return 403)
  if (bannerSession?.banners) {
    const completedBanners = bannerSession.banners.filter(
      (b: any) => b.status === 'completed' && b.image_url
    );
    completedBanners.forEach((b: any, i: number) => {
      banners.push({
        banner_id: i,
        image_url: b.image_url,
        appeal_segment_id: 0,
        appeal_id: 0,
        size: '1200x628',
        style_key: b.style_key,
        visibility: i < 2 ? 'public' : 'locked',
      });
    });
    return banners;
  }

  // Priority 3: legacy banners in report_data
  if (legacyBanners) {
    legacyBanners.forEach((b: any, i: number) => {
      if (b.image_url) {
        banners.push({
          banner_id: i,
          image_url: b.image_url,
          appeal_segment_id: 0,
          appeal_id: 0,
          size: '1200x628',
          style_key: b.title,
          visibility: i < 2 ? 'public' : 'locked',
        });
      }
    });
  }

  return banners;
}

/**
 * Build USP from planning data (fallback when v2_insights not available).
 */
function buildFallbackUsp(planning: any): {
  usp_statement: string;
  pod: string[];
  pop: string[];
} {
  return {
    usp_statement:
      planning?.key_copies?.headlines?.[0] ?? planning?.headlines?.[0] ?? '',
    pod: filterGarbled(planning?.product_features?.features ?? planning?.features ?? []),
    pop: filterGarbled(
      planning?.competitor_info?.competitor_styles ?? planning?.competitor_styles ?? []
    ),
  };
}

/**
 * Build segments from v2_insights data.
 */
function buildInsightSegments(insightSegments: any[]): Segment[] {
  return insightSegments.map((seg: any, i: number) => ({
    segment_id: seg.segment_id ?? i + 1,
    name: cleanText(seg.name ?? ''),
    funnel_stage: seg.funnel_stage ?? '',
    visibility: i === 0 ? 'public' as const : 'locked' as const,
    demographics: {
      gender: seg.demographics?.gender ?? '',
      age_range: seg.demographics?.age_range ?? '',
      occupation: seg.demographics?.occupation ?? '',
      purchasing_power: seg.demographics?.purchasing_power ?? '',
    },
    emotion_map: {
      pain: cleanText(seg.emotion_map?.pain ?? ''),
      ideal_state: cleanText(seg.emotion_map?.ideal_state ?? ''),
      barriers: cleanText(seg.emotion_map?.barriers ?? ''),
      purchase_drivers: cleanText(seg.emotion_map?.purchase_drivers ?? ''),
    },
    benefits: {
      functional: cleanText(seg.benefits?.functional ?? ''),
      emotional: cleanText(seg.benefits?.emotional ?? ''),
      self_actualization: cleanText(seg.benefits?.self_actualization ?? ''),
    },
  }));
}

/**
 * Build appeal strategies from v2_insights data.
 */
function buildInsightStrategies(insightStrategies: any[]): AppealStrategy[] {
  return insightStrategies.map((strat: any, i: number) => ({
    segment_id: strat.segment_id ?? 0,
    appeal_id: strat.appeal_id ?? i,
    name: cleanText(strat.name ?? ''),
    type: strat.type ?? '',
    direction: cleanText(strat.direction ?? ''),
    insight_reason: cleanText(strat.insight_reason ?? ''),
    strategy_detail: cleanText(strat.strategy_detail ?? ''),
    copy_examples: filterGarbled(strat.copy_examples ?? []),
    visibility: i === 0 ? 'public' as const : 'locked' as const,
  }));
}

/**
 * Transform backend API response to the v0 ReportData format.
 * Handles both new (analysis/planning) and legacy field structures.
 * Prefers v2_insights (AI-generated) when available, falls back to planning data.
 */
export function transformReportData(backendData: any): TransformedReport {
  const reportData = backendData.report_data ?? {};
  const analysis = reportData.analysis ?? {};
  const planning = reportData.planning ?? {};
  const insights = reportData.v2_insights;
  const bannerSession = backendData.banner_session ?? {};

  // USP: prefer v2_insights, fallback to planning
  const usp = insights?.usp
    ? {
        usp_statement: cleanText(insights.usp.usp_statement ?? ''),
        pod: filterGarbled(insights.usp.pod ?? []),
        pop: filterGarbled(insights.usp.pop ?? []),
      }
    : {
        ...buildFallbackUsp(planning),
        usp_statement:
          planning.key_copies?.headlines?.[0] ??
          planning.headlines?.[0] ??
          analysis.title ??
          '',
      };

  // Segments: prefer v2_insights, fallback to planning
  const segments =
    insights?.segments && insights.segments.length > 0
      ? buildInsightSegments(insights.segments)
      : buildSegments(planning);

  // Appeal strategies: prefer v2_insights, fallback to planning
  const appealStrategies =
    insights?.appeal_strategies && insights.appeal_strategies.length > 0
      ? buildInsightStrategies(insights.appeal_strategies)
      : buildAppealStrategies(planning);

  // Competitor map, appeal map, and banner evaluations from v2_insights
  const competitorMap: CompetitorMap | undefined = insights?.competitor_map ?? undefined;
  const appealMap: AppealMap | undefined = insights?.appeal_map ?? undefined;
  const bannerEvaluations: BannerEvaluation[] = insights?.banner_evaluations ?? [];

  return {
    meta: {
      company_name: backendData.company_name ?? analysis.title ?? '',
      service_url: backendData.service_url ?? reportData.service_url ?? analysis.domain ?? '',
      created_at: backendData.created_at ?? '',
    },
    service_info: {
      product_name: cleanText(analysis.title ?? ''),
      brand: cleanText(
        planning.brand_info?.brand_name ?? planning.brand_name ?? analysis.title ?? ''
      ),
      category: analysis.category ?? '',
      market_type: insights?.service_info?.market_type ?? (analysis.category === 'SaaS・IT' || analysis.category === '人材' ? 'B2B' : 'B2C'),
      sales_model: insights?.service_info?.sales_model ?? '',
      catchphrase: cleanText(extractFirstSentence(analysis.description)),
      description: cleanText(analysis.description ?? ''),
      logo_url: analysis.logo_url ?? null,
      brand_colors: analysis.colors ?? [],
    },
    usp,
    segments,
    appeal_strategies: appealStrategies,
    banners: buildBanners(bannerSession, backendData.banner_urls, reportData.banners),
    is_unlocked: backendData.is_unlocked ?? false,
    competitor_map: competitorMap,
    appeal_map: appealMap,
    banner_evaluations: bannerEvaluations,
  };
}
